import React from "react";
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Button,
  Box,
} from "@mui/material";
import BookOnlineIcon from "@mui/icons-material/BookOnline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

export default function ServiceCardDefault({
  service,
  onBookNow,
  isServiceCollection = false,
  maxWidth = "auto",
}) {
  return (
    <Card
      sx={{
        height: "100%",
        minHeight: { xs: 260, sm: 300 },
        display: "flex",
        flexDirection: "column",
        maxWidth: maxWidth,
        width: "100%",
        mx: "auto",
        borderRadius: 3,
        position: "relative",
        overflow: "hidden",
        cursor: "pointer",
        background: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(30,30,40,0.8)"
            : "rgba(255,255,255,0.9)",
        backdropFilter: "blur(16px)",
        border: "1px solid",
        borderColor: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.1)"
            : "rgba(0,0,0,0.05)",
        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? "0 8px 32px rgba(0,0,0,0.3)"
            : "0 8px 32px rgba(0,0,0,0.08)",
        transform: "translateY(0)",
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: (theme) =>
            theme.palette.mode === "dark"
              ? `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.secondary.main}10 100%)`
              : `linear-gradient(135deg, ${theme.palette.primary.main}05 0%, ${theme.palette.secondary.main}05 100%)`,
          opacity: 0,
          transition: "opacity 0.3s ease",
          zIndex: 0,
        },
        "&:hover": {
          transform: "translateY(-8px)",
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 20px 60px rgba(0,0,0,0.4)"
              : "0 20px 60px rgba(0,0,0,0.15)",
          borderColor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.2)"
              : `${theme.palette.primary.main}30`,
          "&::before": {
            opacity: 1,
          },
          "& .service-image": {
            transform: "scale(1.05)",
          },
        },
        "& > *": {
          position: "relative",
          zIndex: 1,
        },
      }}
    >
      <CardMedia
        component="img"
        height={140}
        image={
          service.thumbnailImage
            ? `${process.env.BASEURL}${service.thumbnailImage}`
            : "https://via.placeholder.com/300x200?text=No+Image"
        }
        alt={service.Service_Full_Name}
        className="service-image"
        sx={{
          transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
          objectFit: "cover",
        }}
      />
      <CardContent
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          p: { xs: 1, sm: 1.5 }, // Reduce padding
        }}
      >
        <div>
          <Typography
            gutterBottom
            variant="h6"
            component="div"
            sx={{
              fontSize: { xs: "0.9rem", sm: "1.05rem" }, // Slightly smaller
              lineHeight: 1.2, // More compact
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              mb: { xs: 0.25, sm: 0.5 }, // Less margin below
            }}
          >
            {service.Service_Short_Name}
          </Typography>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{
              mb: { xs: 0.25, sm: 0.5 }, // Less margin below
              fontSize: { xs: "0.72rem", sm: "0.82rem" }, // Slightly smaller
              display: { xs: "none", sm: "block" },
              lineHeight: 1.3, // More compact
            }}
          >
            {service.Service_Full_Name}
          </Typography>
        </div>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            mt: { xs: 0.25, sm: 0.25 }, // Minimal spacing
            gap: 0.5, // Compact vertical gap between price and button
          }}
        >
          {isServiceCollection && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontWeight: 400,
                mb: 0,
                fontSize: { xs: "0.68rem", sm: "0.8rem" },
              }}
            >
              Starting from
            </Typography>
          )}
          <Typography
            variant="h6"
            color="primary"
            sx={{
              mt: isServiceCollection ? 0 : 0,
              fontSize: { xs: "0.88rem", sm: "1rem" }, // Slightly smaller
              fontWeight: "bold",
              lineHeight: 1.2,
            }}
          >
            ₹
            {isServiceCollection
              ? parseFloat(service.minPrice).toLocaleString("en-IN")
              : parseFloat(service.Selling_Price).toLocaleString("en-IN")}
          </Typography>
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            startIcon={<BookOnlineIcon className="button-icon" />}
            onClick={() => onBookNow(service)}
            sx={{
              mt: 0.5,
              alignSelf: "stretch",
              borderRadius: 2,
              py: 0.5,
              px: 1.5,
              fontWeight: 600,
              borderWidth: "2px",
              position: "relative",
              overflow: "hidden",
              background: "linear-gradient(45deg, transparent, transparent)",
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              whiteSpace: "nowrap",
              fontSize: { xs: "0.72rem", sm: "0.85rem" },
              minHeight: { xs: "28px", sm: "36px" },
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: "-100%",
                width: "100%",
                height: "100%",
                background: (theme) =>
                  `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 50%, ${theme.palette.primary.light} 100%)`,
                transition: "left 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                zIndex: -1,
              },
              "&:hover": {
                color: "white",
                borderColor: "transparent",
                transform: "translateY(-2px)",
                boxShadow: (theme) =>
                  `0 12px 30px ${theme.palette.primary.main}40, 0 6px 20px ${theme.palette.secondary.main}30`,
                "&::before": {
                  left: 0,
                },
                "& .button-icon": {
                  transform: "translateX(8px) scale(1.1)",
                  opacity: 0,
                },
                "& .button-text": {
                  transform: "translateX(-8px)",
                },
                "& .hover-icon": {
                  transform: "translateX(0px) scale(1.1)",
                  opacity: 1,
                },
              },
              "&:active": {
                transform: "translateY(0px)",
              },
              "& .button-icon": {
                fontSize: "1.15rem",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                transform: "translateX(0px) scale(1)",
                opacity: 1,
              },
              "& .button-text": {
                transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                transform: "translateX(0px)",
                display: "inline",
              },
              "& .hover-icon": {
                position: "absolute",
                left: "16px",
                fontSize: "1.15rem",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                transform: "translateX(-8px) scale(1)",
                opacity: 0,
              },
            }}
          >
            <ArrowForwardIcon className="hover-icon" />
            <span className="button-text">Book Now</span>
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
